import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useBoard } from '../hooks/useBoard'
import PickBoard from '../components/PickBoard'
import BurnGrid from '../components/BurnGrid'
import Standings from '../components/Standings'
import Insights from '../components/Insights'
import ScoringSettings from '../components/ScoringSettings'
import Roster from '../components/Roster'
import Wordmark from '../components/Wordmark'
import WeekBar from '../components/WeekBar'

type Tab = 'pick' | 'burn' | 'standings' | 'insights' | 'league' | 'scoring'

const TAB_LABEL: Record<Tab, string> = {
  pick: 'Lineup', burn: 'Burn board', standings: 'Standings',
  insights: 'Insights', league: 'League', scoring: 'Scoring',
}

export default function LeagueView() {
  const { leagueId } = useParams()
  const {
    league, me, weeks, weekId, setWeekId, board, myPicks, loading, error, setPick, clearPick,
    refreshWeek,
  } = useBoard(leagueId)
  const [tab, setTab] = useState<Tab>('pick')

  if (loading) return <div className="center muted">Loading league…</div>
  if (!league) return <div className="center muted">League not found.</div>

  const week = weeks.find((w) => w.id === weekId)

  return (
    <div className="page">
      <header className="topbar">
        <Link className="wordmark" to="/app"><Wordmark /></Link>
        <div className="row gap">
          <span className="league-name">{league.name}</span>
          <code className="code">{league.join_code}</code>
        </div>
      </header>

      <div className="subbar container">
        <select value={weekId ?? ''} onChange={(e) => setWeekId(e.target.value)}>
          {weeks.map((w) => (
            <option key={w.id} value={w.id}>
              {w.phase === 'playoff' ? '★ ' : ''}{w.label ?? `Week ${w.week_no}`}
            </option>
          ))}
        </select>
        <nav className="tabs">
          {(['pick', 'burn', 'standings', 'insights', 'league', 'scoring'] as Tab[]).map((t) => (
            <button
              key={t}
              className={tab === t ? 'tab active' : 'tab'}
              onClick={() => setTab(t)}
            >
              {TAB_LABEL[t]}
            </button>
          ))}
        </nav>
      </div>

      <main className="container">
        {week?.phase === 'playoff' && league.playoff_reset && (
          <div className="banner">Playoff phase — the burn board resets. Everything is live again.</div>
        )}

        {tab === 'pick' && (
          <>
            <WeekBar
              week={week}
              filled={myPicks.length}
              required={league.roster_slots.length}
              unitsLeft={board.filter((b) => !b.burned).length}
            />
            <PickBoard
            slots={league.roster_slots}
            board={board}
            myPicks={myPicks}
            onPick={setPick}
            onClear={clearPick}
              error={error}
            />
          </>
        )}
        {tab === 'burn' && <BurnGrid board={board} />}
        {tab === 'standings' && <Standings leagueId={league.id} weekId={weekId} />}
        {tab === 'insights' && (
          <Insights leagueId={league.id} weekId={weekId} memberId={me?.id ?? null} />
        )}
        {tab === 'league' && (
          <Roster
            league={league}
            weekId={weekId}
            isCommissioner={me?.role === 'commissioner'}
            onChanged={() => void refreshWeek()}
          />
        )}
        {tab === 'scoring' && (
          <ScoringSettings
            leagueId={league.id}
            isCommissioner={me?.role === 'commissioner'}
            initial={league.position_weights}
          />
        )}
      </main>
    </div>
  )
}
